import { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CASES, getCase } from "../data/portfolio";
import { IconChevronLeft, IconChevronRight } from "../components/icons";
import { DEEP_DIVES } from "../data/deepdives";

export function PortfolioCase() {
  const { slug } = useParams<{ slug: string }>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const c = slug ? getCase(slug) : undefined;

  if (!c) return <Navigate to="/portfolio" replace />;

  const index = CASES.findIndex((x) => x.slug === c.slug);
  const prev = CASES[(index - 1 + CASES.length) % CASES.length];
  const next = CASES[(index + 1) % CASES.length];
  const DeepDive = DEEP_DIVES[c.slug];

  return (
    <div className="portfolio-page">
      <nav className="pf-nav">
        <div className="pf-container pf-nav-inner">
          <Link to="/portfolio" className="pf-brand">
            <span className="pf-brand-mark">A</span>
            <span>AUTOMATRIX</span>
          </Link>
          <div className="pf-nav-links">
            <Link to="/portfolio#work">Selected Work</Link>
            <Link to="/portfolio#stack">Stack</Link>
          </div>
          <a className="btn primary btnlink" href="mailto:lucas@automatrix-ia.com">
            Get in touch
          </a>
        </div>
      </nav>

      <div className="pf-container">
        <Link to="/portfolio" className="pf-back-link">
          ← All work
        </Link>
      </div>

      <header className="pf-case-hero">
        <div className="pf-container">
          <div className="pf-case-card-meta">
            <span className="pf-case-tag">{c.category}</span>
            {c.region && <span className="pf-case-region">{c.region}</span>}
          </div>
          <h1 className="pf-case-title">{c.title}</h1>
          {c.client && <p className="pf-case-client-line">Client: {c.client}</p>}
          <p className="lead-copy pf-case-hero-tagline">{c.tagline}</p>
        </div>
      </header>

      <section className="pf-section pf-section-tight">
        <div className="pf-container">
          {c.videoSrc ? (
            <div className="pf-video-shell">
              <div className="pf-video-stage">
                <video key={c.videoSrc} ref={videoRef} controls playsInline preload="metadata" poster={c.thumbnail}>
                  <source src={c.videoSrc} type="video/mp4" />
                  Your browser does not support video.
                </video>
              </div>
            </div>
          ) : (
            <div className="pf-case-visual-large" style={{ background: c.accent }} />
          )}

          <div className="pf-project-detail pf-video-shell">
            <h4>About this project</h4>
            <p>{c.description}</p>
          </div>

          <div className="pf-highlights">
            {c.highlights.map((h) => (
              <div className="pf-highlight" key={h}>
                <span className="pf-highlight-dot" />
                <span>{h}</span>
              </div>
            ))}
          </div>

          <div className="pf-project-detail pf-video-shell">
            <h4>Stack used</h4>
            <div className="pf-pill-row">
              {c.stack.map((s) => (
                <span className="pf-pill" key={s}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {DeepDive && (
        <section className="pf-section pf-section-tight pf-deepdive">
          <div className="pf-container">
            <DeepDive />
          </div>
        </section>
      )}

      <section className="pf-section pf-case-nav-section">
        <div className="pf-container pf-case-nav-grid">
          <Link to={`/portfolio/${prev.slug}`} className="pf-case-nav-link">
            <IconChevronLeft />
            <div>
              <span className="pf-case-nav-label">Previous</span>
              <strong>{prev.title}</strong>
            </div>
          </Link>
          <Link to={`/portfolio/${next.slug}`} className="pf-case-nav-link pf-case-nav-link-end">
            <div>
              <span className="pf-case-nav-label">Next</span>
              <strong>{next.title}</strong>
            </div>
            <IconChevronRight />
          </Link>
        </div>
      </section>

      <footer className="pf-footer">
        <div className="pf-container pf-footer-grid">
          <div>
            <strong>AUTOMATRIX</strong>
            <br />
            AI infrastructure · automation architecture · agents
          </div>
          <div>Lucas F. N. Alves · Brazil · UTC−3</div>
        </div>
      </footer>
    </div>
  );
}
